
import BarcodeRow from '@/components/barcode/BarcodeRow.vue'
import BarcodeRowAdjustment from '@/components/barcode/BarcodeRowAdjustment.vue'
import BarcodeRowEsign from '@/components/barcode/BarcodeRowEsign.vue'
import BarcodeRowImage from '@/components/barcode/BarcodeRowImage.vue'
import BarcodeRowMulti from '@/components/barcode/BarcodeRowMulti.vue'
import { CUSTOM_FIELD_KEY, TIMETABLE_DATETIME } from '@/utils/constants'
import moment from 'moment'
import { format } from 'date-fns'
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
// import { EFileKind } from '@/utils/enums'
import {
  IBarcodeCustomField,
  ICustomFieldFileView,
  IFileItem,
  IListStringItem,
  IProjectCustomField,
} from '@/utils/types'
import { EFileKind } from '@/utils/enums'

export interface IField {
  label: string
  value: string | string[] | IListStringItem[]
  hidden: boolean
  fieldType?: string
}

@Options({
  components: { BarcodeRow, BarcodeRowImage, BarcodeRowMulti, BarcodeRowEsign, BarcodeRowAdjustment },
  name: 'BarcodeCustomField',
})
export default class BarcodeCustomField extends Vue {
  @Prop({ type: Object }) readonly barcodeCustomFields!: Record<string, IBarcodeCustomField>
  @Prop({ type: Object }) readonly projectCustomFields!: Record<string, IProjectCustomField>

  listStringModalVisible = false
  listStringData: IListStringItem[] = []
  currentFieldLabel = ''

  get customFieldStrings() {
    const customFields = [] as IField[]
    for (const key in this.barcodeCustomFields) {
      if (this.projectCustomFields?.[key]) {
        let value: string | string[] | IListStringItem[] = ''
        const fieldType = this.barcodeCustomFields[key].fieldType
        switch (fieldType) {
          case CUSTOM_FIELD_KEY.text:
            value = this.barcodeCustomFields[key]?.text || ''
            break
          case CUSTOM_FIELD_KEY.number:
            value = (this.barcodeCustomFields[key]?.number || '').toString()
            break
          case CUSTOM_FIELD_KEY.singleSelect:
            value = this.barcodeCustomFields[key]?.singleSelect?.value || ''
            break
          case CUSTOM_FIELD_KEY.multiSelect:
            value = (this.barcodeCustomFields[key]?.multiSelect || []).map((item) => item.value).join(', ')
            break
          case CUSTOM_FIELD_KEY.date:
            value = this.barcodeCustomFields[key]?.date || ''
            break
          case CUSTOM_FIELD_KEY.dateTimeData:
            value = this.barcodeCustomFields[key]?.dateTimeData
              ? moment(this.barcodeCustomFields[key]?.dateTimeData)
                  .local()
                  .format(TIMETABLE_DATETIME.DATE_TIME_FULL_FORMAT)
              : ''
            break
          case CUSTOM_FIELD_KEY.phoneNumber:
            value = this.barcodeCustomFields[key]?.phoneNumber?.number || ''
            break
          case CUSTOM_FIELD_KEY.email:
            value = this.barcodeCustomFields[key]?.email || ''
            break
          case CUSTOM_FIELD_KEY.listString:
            value = ((this.barcodeCustomFields[key]?.listString || []) as unknown) as IListStringItem[]
            break
        }
        if (value && (Array.isArray(value) ? value.length > 0 : value !== '')) {
          customFields.push({
            label: this.projectCustomFields?.[key].label,
            hidden: !!this.projectCustomFields?.[key].isHidden,
            value,
            fieldType,
          })
        }
      }
    }

    return customFields
  }

  isListStringField(fieldType?: string) {
    return fieldType === CUSTOM_FIELD_KEY.listString
  }

  showListStringModal(value: IListStringItem[], label: string) {
    if (Array.isArray(value)) {
      this.listStringData = value.map((item) => {
        if (typeof item === 'string') {
          return {
            code: item,
            limitAmount: undefined,
            currentAmount: undefined,
            unit: undefined,
            createdDateTime: undefined,
          }
        }
        return item
      })
    } else {
      this.listStringData = []
    }
    this.currentFieldLabel = label
    this.listStringModalVisible = true
  }

  formatDateTime(dateTime: string) {
    if (!dateTime) return ''
    try {
      return format(new Date(dateTime), 'yyyy/MM/dd HH:mm:ss')
    } catch (error) {
      return dateTime
    }
  }

  getListStringLength(index: number): number {
    const field = this.customFieldStrings[index]
    if (field && Array.isArray(field.value)) {
      return field.value.length
    }
    return 0
  }

  handleListStringClick(index: number) {
    const field = this.customFieldStrings[index]
    if (field && Array.isArray(field.value)) {
      this.showListStringModal(field.value as IListStringItem[], field.label)
    }
  }

  get customFieldAdjustments() {
    const customFields = [] as Array<Record<string, string>>
    if (this.barcodeCustomFields) {
      const adjustments = Object.entries(this.barcodeCustomFields).filter(
        (item) => item[1].fieldType === CUSTOM_FIELD_KEY.adjustment
      )
      adjustments.forEach((item) => {
        const cf = {
          key: item[0],
          label: this.projectCustomFields[item[0]].label,
          adjustment: '',
          currentValue: '',
        }
        if (item[1].adjustment) {
          cf.adjustment = item[1].adjustment.toString()
        }
        if (item[1].currentValue) {
          cf.currentValue = item[1].currentValue.toString()
        }
        customFields.push(cf)
      })
    }
    return customFields
  }

  get customFieldEsigns() {
    const customFields = [] as Array<Record<string, string>>
    if (this.barcodeCustomFields) {
      const eSigns = Object.entries(this.barcodeCustomFields).filter(
        (item) => item[1].fieldType === CUSTOM_FIELD_KEY.eSign
      )
      eSigns.forEach((item) => {
        if (item[1].eSign) {
          customFields.push({
            label: this.projectCustomFields[item[0]].label,
            value: item[1].eSign || '',
          })
        }
      })
    }
    return customFields
  }

  get customFieldImages() {
    const customFields = [] as Array<ICustomFieldFileView & { hidden: boolean }>
    if (this.barcodeCustomFields) {
      const files = Object.entries(this.barcodeCustomFields).filter(
        (item) => item[1].fieldType === CUSTOM_FIELD_KEY.files
      )
      files.forEach((item) => {
        if (item[1]?.files && item[1]?.files.length > 0) {
          const file = this.projectCustomFields?.[item[0]]
          if (file.label && file.fileKind === EFileKind.IMAGE) {
            customFields.push({
              label: this.projectCustomFields?.[item[0]].label,
              files: item[1]?.files || [],
              hidden: !!this.projectCustomFields?.[item[0]].isHidden,
            })
          }
        }
      })
    }

    return customFields
  }

  get customFieldFiles() {
    const customFields = [] as Array<ICustomFieldFileView & { hidden: boolean }>
    if (this.barcodeCustomFields) {
      const files = Object.entries(this.barcodeCustomFields).filter(
        (item) => item[1].fieldType === CUSTOM_FIELD_KEY.files
      )
      files.forEach((item) => {
        if (item[1]?.files && item[1]?.files.length > 0) {
          const file = this.projectCustomFields?.[item[0]]
          if (file.label && file.fileKind !== EFileKind.IMAGE) {
            customFields.push({
              label: this.projectCustomFields?.[item[0]].label,
              files: item[1]?.files || [],
              hidden: !!this.projectCustomFields?.[item[0]].isHidden,
            })
          }
        }
      })
    }

    return customFields
  }

  get customFieldReferences() {
    const customFields = [] as Array<ICustomFieldFileView & { hidden: boolean }>
    if (this.barcodeCustomFields) {
      const references = Object.entries(this.barcodeCustomFields).filter(
        (item) => item[1].fieldType === CUSTOM_FIELD_KEY.reference
      )
      references.forEach((item) => {
        if (item[1].reference) {
          const file = item?.[1]?.reference?.file || {
            path: '',
            url: '',
            name: '',
          }
          customFields.push({
            label: this.projectCustomFields[item[0]].label,
            text: item[1]?.reference.text,
            files: [file as IFileItem],
            hidden: !!this.projectCustomFields?.[item[0]].isHidden,
          })
        }
      })
    }
    return customFields
  }

  renderBarcodeRow(index: number) {
    const field = this.customFieldStrings[index]
    if (!field) return null

    return {
      labelWidth: 40,
      label: field.label,
      hidden: field.hidden,
      value: field.value,
    }
  }

  renderBarcodeRowReference(index: number) {
    const field = this.customFieldReferences[index]
    if (!field) return null

    return {
      labelWidth: 40,
      label: field.label,
      hidden: field.hidden,
      value: field.text,
    }
  }
}
